import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import TopMenu from './Top_Menu';
import Bottom from './Bottom';
import en from '../../language/Eng.json';

const isMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

const FontFaceStyle = createGlobalStyle`
  @font-face {
    font-family: 'Inkfree';
    src: url('/font/Inkfree.ttf') format('truetype');
    font-weight: normal;
    font-style: normal;
    font-display: swap;
  }
`;

const PageWrapper = styled.div`
  background-color: #F4F1E6;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const MainContent = styled.main`
  flex: 1;
  display: flex;
  align-items: center;
  padding: 120px 6vw 80px;
  box-sizing: border-box;
  font-family: 'Inkfree', sans-serif;

  @media (max-width: 768px) {
    padding: 100px 20px 60px;
    align-items: flex-start;
  }

  /* 본문 영역 내의 모든 요소에만 폰트 적용 */
  &, & * {
    font-family: 'Inkfree', sans-serif;
  }
`;

const LayoutGrid = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 80px;
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 60px;
  }
`;

const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const RightSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px 20px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const Tag = styled.div`
  font-size: 12px;
  letter-spacing: 2px;
  color: #5B2C16;
  margin-bottom: 20px;
`;

const MainTitle = styled.h1`
  font-size: clamp(32px, 4vw, 56px);
  font-weight: 400;
  line-height: 1.2;
  color: #5B2C16;
  margin: 0;
  max-width: 800px;
  white-space: pre-line;
`;

const BookButton = styled.div`
  font-size: 14px;
  letter-spacing: 2px;
  color: #5B2C16;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 15px;
  margin-top: 20px;
  
  &:hover { opacity: 0.7; }
`;

const InfoGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InfoLabel = styled.div`
  font-size: 11px;
  letter-spacing: 1px;
  color: #a69c8a;
`;

const InfoValue = styled.div`
  font-size: 13px;
  color: #5B2C16;
  font-weight: bold;
  white-space: pre-line;
  word-break: keep-all;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const SocialArea = styled.div`
  grid-column: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-end;
  gap: 15px;

  @media (max-width: 768px) {
    grid-column: 1;
    align-items: flex-start;
  }
`;

const InstagramIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
  </svg>
);

const FacebookIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
  </svg>
);

const Contact: React.FC = () => {
  const { contactPage, bottom } = en;

  const handlePhoneClick = (e: React.MouseEvent, phone: string) => {
    e.preventDefault();
    if (isMobile()) {
      window.location.href = `tel:${phone.replace(/\s+/g, '')}`;
    } else {
      navigator.clipboard.writeText(phone).then(() => {
        alert('복사되었습니다');
      }).catch(() => {
        alert('복사되었습니다');
      });
    }
  };

  const handleEmailClick = (e: React.MouseEvent, email: string) => {
    e.preventDefault();
    if (isMobile()) {
      window.location.href = `mailto:${email}`;
    } else {
      window.open(`https://mail.google.com/mail/?view=cm&fs=1&to=${email}`, '_blank');
    }
  };

  const handleAddressClick = (e: React.MouseEvent, address: string) => {
    e.preventDefault();
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address.replace(/\n/g, ' '))}`, '_blank');
  };

  return (
    <>
      <FontFaceStyle />
      <PageWrapper>
        <TopMenu />
        <MainContent>
          <LayoutGrid>
            <LeftSection>
              <div>
                <Tag>{contactPage.titleTag}</Tag>
                <MainTitle>{contactPage.mainTitle}</MainTitle>
              </div>
            </LeftSection>
            <RightSection>
              <InfoGroup>
                <InfoLabel>{contactPage.sections.general}</InfoLabel>
                <InfoValue onClick={(e) => handleEmailClick(e, contactPage.emails.general)}>{contactPage.emails.general}</InfoValue>
              </InfoGroup>
              <InfoGroup>
                <InfoLabel>{contactPage.sections.phone}</InfoLabel>
                <InfoValue onClick={(e) => handlePhoneClick(e, bottom.phone)}>{bottom.phone}</InfoValue>
              </InfoGroup>
              <InfoGroup style={{ gridColumn: '1 / -1' }}>
                <InfoLabel>{contactPage.sections.findUs}</InfoLabel>
                <InfoValue onClick={(e) => handleAddressClick(e, bottom.address)}>{bottom.address}</InfoValue>
              </InfoGroup>
              <SocialArea>
                <InfoLabel>{contactPage.sections.followUs}</InfoLabel>
                <div style={{ display: 'flex', gap: '15px', color: '#5B2C16' }}>
                  <InstagramIcon />
                  <FacebookIcon />
                </div>
              </SocialArea>
            </RightSection>
          </LayoutGrid>
        </MainContent>
        <Bottom />
      </PageWrapper>
    </>
  );
};

export default Contact;